import api from "../../../api";
import { User } from "../../../types/User";
import { getBackendUrl } from "../../../util/envFn";
import { Job } from "../../Jobs/types";
import { FormatDefinition } from "./types";

export const formatDate = (date: string): Date => {
    // Handle empty or invalid input
    if (!date || typeof date !== 'string') {
        return new Date();
    }

    // Clean up the input string
    const cleanDate = date.trim().replace(/\s+/g, '');

    // Define regex patterns for different date formats
    const patterns = [
        // DD-MM月-YY or DD-MM月-YYYY (Chinese month format)
        /^(\d{1,2})[/-](\d{1,2})月[/-](\d{2}|\d{4})$/,
        // DD-MM-YY or DD-MM-YYYY
        /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/,
        // YYYY-MM-DD
        /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/
    ];

    for (const pattern of patterns) {
        const match = cleanDate.match(pattern);
        if (match) {
            let [_, part1, part2, part3] = match;

            // Handle 2-digit year
            if (part3.length === 2) {
                const twoDigitYear = parseInt(part3);
                // Assume years 00-49 are 2000s, 50-99 are 1900s
                part3 = (twoDigitYear < 50 ? '20' : '19') + part3;
            }

            // If the pattern is YYYY-MM-DD
            if (part1.length === 4) {
                return new Date(`${part1}-${part2.padStart(2, '0')}-${part3.padStart(2, '0')}`);
            }

            // For all other patterns (DD-MM-YYYY)
            return new Date(
                `${part3}-${part2.padStart(2, '0')}-${part1.padStart(2, '0')}`
            );
        }
    }

    console.warn(`Invalid date format: ${date}, using current date instead`);
    return new Date();
};

export const createJobDescription = (item: any): string => {
    const description = item.description;
    const aboutCompany = item.aboutCompany;
    const requirements = item.requirements;
    const packageOffer = item.package;
    const highlights = item.highlights;

    return `
        ${description && "\n<b style='line-height: 1.5rem;'>Description:</b> "}
        ${description}
        ${aboutCompany && "\n<b style='line-height: 1.5rem;'>About Company:</b> "}
        ${aboutCompany}
        ${requirements && "\n<b style='line-height: 1.5rem;'>Requirements:</b> "}
        ${requirements}
        ${packageOffer && "\n<b style='line-height: 1.5rem;'>Package Offer:</b> "}
        ${packageOffer}
        ${highlights && "\n<b style='line-height: 1.5rem;'>Highlights:</b> "}
        ${highlights}
    `;
};

const createUserDescription = (item: any): string => {
    const description = item.description;
    const interest = item.interest;

    return `
        ${description && "\n<b style='line-height: 1.5rem;'>Description:</b> "}
        ${description}
        ${interest && "\n<b style='line-height: 1.5rem;'>Interest:</b> "}
        ${interest}
    `;
}

const createUserWorkHistory = (item: any): string => {
    const workHistory = item.workHistory;
    const achievement = item.achievement;

    return `    
        ${workHistory && achievement && "\n<b style='line-height: 1.5rem;'>Work History:</b> "}
        ${workHistory}
        ${workHistory && achievement && "\n<b style='line-height: 1.5rem;'>Achievement:</b> "}
        ${achievement}
    `;
}

const getBirthDateByAge = (age: number): string => {
    const currentYear = new Date().getFullYear();
    const birthYear = currentYear - age;
    return new Date(birthYear, 0, 1).toISOString();
}

const validateData = (item: any, format: FormatDefinition, emailSet: Set<string>): void => {
    Object.entries(format.columns).forEach(([key, col]) => {
        const value = item[col.name];
        if (col.required && (value === undefined || value === ''))
            throw new Error(`Required field "${col.name}" is missing, please check the file`);

        if (col.type === 'email') {
            if (emailSet.has(value))
                throw new Error(`Duplicate email found: ${value}, please check the file`);
            emailSet.add(value);
        }
    });
};

const createSkills = (item: any): string => {
    const skills = item.skills
        .split('\n')
        .map((skill: string) => skill.trim().replace(/,/g, ' |'))
        .filter((skill: string) => skill.length > 0);

    return skills.join(', ');
}

export type ExcelData = (Job | ExcelUserData) & { duplicated?: boolean };
export type ExcelUserData = User & { duplicated?: boolean };

export const mapExcelData = async (
    jsonData: any[],
    format: FormatDefinition,
    importType: 'jobs' | 'users'
): Promise<ExcelData[]> => {
    const emailSet = new Set<string>();

    const mappedData = jsonData.map((item: any) => {
        const mappedItem: any = {};

        validateData(item, format, emailSet);

        Object.entries(format.columns).forEach(([key, col]) => {
            const value = item[col.name];
            if (col.type === 'number') {
                mappedItem[key] = value !== undefined ? Number(value) : undefined;
            } else {
                mappedItem[key] = value !== undefined ? value : '';
            }
        });

        if (importType === 'jobs') {
            return {
                title: mappedItem.title,
                description: createJobDescription(mappedItem),
                location: mappedItem.location,
                category: mappedItem.category,
                type: mappedItem.type,
                salary: mappedItem.salary,
                company: mappedItem.company,
                createdAt: formatDate(mappedItem.createdAt).toISOString(),
            } as Job;
        } else {
            return {
                fullName: mappedItem.fullName,
                email: mappedItem.email,
                gender: mappedItem.gender,
                birthDate: mappedItem.birthDate ? mappedItem.birthDate : mappedItem.age ? getBirthDateByAge(mappedItem.age) : undefined,
                jobTitle: mappedItem.jobTitle,
                jobDescription: mappedItem.jobDescription,
                skills: createSkills(mappedItem),
                description: createUserDescription(mappedItem),
                jobLookingFor: mappedItem.jobLookingFor,
                jobLookingForType: mappedItem.jobLookingForType,
                jobLookingForSalary: mappedItem.jobLookingForSalary,
                jobLookingForLocation: mappedItem.jobLookingForLocation,
                education: mappedItem.education,
                workHistory: createUserWorkHistory(mappedItem),
            } as ExcelUserData;
        }
    });

    if (mappedData.length === 0)
        throw new Error('Empty data found, please check the file');

    const existingEmails = await getExistingEmails(mappedData.map((item: any) => item.email));
    const existingEmailsSet = new Set<string>(existingEmails);

    mappedData.forEach((item: any) => {
        if (existingEmailsSet.has(item.email))
            item.duplicated = true;
    });

    return mappedData;
};

export const getExistingEmails = async (emails: string[]) => {
    if (emails.length === 0)
        return [];

    const response = await api.get(getBackendUrl('/users/get-existing-emails'), { params: { emails } })
        .then(res => res.data)
        .catch(err => {
            console.error(err);
            throw new Error('Server error, please try again later');
        });
    console.log(response);
    return response;
};

